import React, { useState } from 'react';

/**
 * Formik-compatible datetime input. 24-hour time only (00–23), no AM/PM.
 * Value is datetime-local string (e.g. "2026-03-12T08:00").
 * App.js formats it to "YYYYMMDDTHHmmssZ" before sending to server.
 */
function DateTimePicker({ field, form, ...props }) {
  const value = field.value || '';
  const [datePart = '', timePart = ''] = value ? value.split('T') : ['', ''];
  const [hourRaw = '', minuteRaw = ''] = timePart ? timePart.split(':') : ['', ''];
  const hourFromForm = hourRaw === '' ? '' : String(parseInt(hourRaw, 10) || 0).padStart(2, '0');
  const minuteFromForm = minuteRaw === '' ? '' : String(parseInt(minuteRaw, 10) || 0).padStart(2, '0');

  const [localHour, setLocalHour] = useState(null);
  const [localMinute, setLocalMinute] = useState(null);

  const hour = localHour !== null ? localHour : hourFromForm;
  const minute = localMinute !== null ? localMinute : minuteFromForm;

  const commitTime = (hRaw, mRaw) => {
    const today = new Date().toISOString().slice(0, 10);
    const hNum = Math.min(23, Math.max(0, parseInt(String(hRaw || '0').replace(/\D/g, ''), 10) || 0));
    const mNum = Math.min(59, Math.max(0, parseInt(String(mRaw || '0').replace(/\D/g, ''), 10) || 0));
    const time = `${String(hNum).padStart(2, '0')}:${String(mNum).padStart(2, '0')}`;
    const next = (datePart || today) + 'T' + time;
    form.setFieldValue(field.name, next);
  };

  const handleDateChange = (e) => {
    const newVal = e.target.value;
    const today = new Date().toISOString().slice(0, 10);
    const h = hour || '00';
    const m = minute || '00';
    const time = `${h}:${m}`;
    const next = newVal ? `${newVal}T${time}` : (time !== '00:00' ? `${today}T${time}` : '');
    form.setFieldValue(field.name, next || '');
  };

  const handleTimeChange = (e) => {
    const { name, value: newVal } = e.target;
    const digitsOnly = String(newVal).replace(/\D/g, '').slice(0, 2);
    if (name === field.name + '_hour') {
      setLocalHour(digitsOnly);
      commitTime(digitsOnly, minute);
    } else if (name === field.name + '_minute') {
      setLocalMinute(digitsOnly);
      commitTime(hour, digitsOnly);
    }
  };

  const handleTimeFocus = (e) => {
    const { name } = e.target;
    if (name === field.name + '_hour' && (hour === '00' || hour === '')) {
      setLocalHour('');
    } else if (name === field.name + '_minute' && (minute === '00' || minute === '')) {
      setLocalMinute('');
    }
  };

  const handleTimeBlur = (e) => {
    setLocalHour(null);
    setLocalMinute(null);
    if (field.onBlur) field.onBlur(e);
  };

  return (
    <div className="datetime-picker-ru" lang="ru">
      <input
        type="date"
        name={field.name + '_date'}
        id={field.name + '_date'}
        value={datePart}
        onChange={handleDateChange}
        onBlur={field.onBlur}
        aria-label="Дата"
        {...props}
      />
      <span className="time-picker-24">
        <input
          type="text"
          inputMode="numeric"
          name={field.name + '_hour'}
          id={field.name + '_hour'}
          value={hour}
          onChange={handleTimeChange}
          onFocus={handleTimeFocus}
          onBlur={handleTimeBlur}
          aria-label="Час (00–23)"
          placeholder="00"
          maxLength={2}
        />
        <span className="time-sep">:</span>
        <input
          type="text"
          inputMode="numeric"
          name={field.name + '_minute'}
          id={field.name + '_minute'}
          value={minute}
          onChange={handleTimeChange}
          onFocus={handleTimeFocus}
          onBlur={handleTimeBlur}
          aria-label="Минуты"
          placeholder="00"
          maxLength={2}
        />
      </span>
    </div>
  );
}

export default DateTimePicker;
